import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, User, Lock } from 'lucide-react';
import axios from "axios";
import { toast } from './lib/toast';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Checkbox } from './components/ui/checkbox';
import apiUrl from './config/config';
import logo from './img/logo.png';
import berries from './img/img.jpg';

const Login = () => {
  const [loading, setLoading] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const token = "Bearer 0j2whpl3*s+2/j-hCl!!-5A-8Corubp5[gqzhGrA.b*hBdO[+r.yGIFpvpOqv";

  const validate = () => {
    const e = {};
    if (!username.trim()) e.username = 'Por favor, ingresa tu usuario';
    if (!password) e.password = 'Por favor, ingresa tu contraseña';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const response = await axios.post(
        `${apiUrl}/ServiceFamilyGrowers.asmx/GetFamilyGrowersProductorLogin`,
        { rut: username, pclave: password },
        {
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
          },
        }
      );

      const responseData = response.data?.d
        ? JSON.parse(response.data.d)
        : response.data;

      if (responseData && responseData.success) {
        const nombreMostrado = responseData.nombreUsuario;
        const idUsuario = responseData.rut;
        localStorage.setItem('isAuthenticated', 'true');
        localStorage.setItem('userName', nombreMostrado);
        localStorage.setItem('idUsuario', idUsuario);

        // Token para el Informe de Aplicaciones (Edge Function informe-aplicaciones).
        // Se limpia primero para que nunca quede el token de una cuenta anterior
        // si el usuario cambia de sesión en el mismo navegador.
        localStorage.removeItem('familyGrowersAccessToken');
        if (typeof responseData.accessToken === 'string' && responseData.accessToken.trim() !== '') {
          localStorage.setItem('familyGrowersAccessToken', responseData.accessToken);
        }

        navigate('/dashboard');
      } else {
        toast.error(responseData?.message || 'Usuario o contraseña incorrectos');
      }
    } catch {
      toast.error('Error al iniciar sesión. Intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-dvh bg-gray-100 grid place-items-center p-6 safe-area">
      <div className="w-full max-w-[1100px] bg-white rounded-2xl overflow-hidden shadow-xl grid grid-cols-1 lg:grid-cols-[1.1fr_1fr]">

        {/* Panel ilustración */}
        <div
          className="hidden lg:flex relative min-h-[520px]"
          style={{
            backgroundImage: `linear-gradient(rgba(0,0,0,.25),rgba(0,0,0,.25)), url(${berries})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 flex flex-col justify-end p-8 text-white">
            <h2 className="text-3xl font-black m-0 tracking-wide drop-shadow-lg">Intranet</h2>
          </div>
        </div>

        {/* Panel formulario */}
        <div className="flex flex-col items-center justify-center p-8 sm:p-12">
          <div className="text-center mb-6">
            <img
              src={logo}
              alt="Logo"
              className="max-h-[120px] sm:max-h-[150px] object-contain mx-auto mb-2"
            />
          </div>

          <form onSubmit={handleSubmit} className="w-full max-w-sm space-y-4" noValidate>
            {/* Username */}
            <div className="space-y-1">
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                <Input
                  type="text"
                  placeholder="Usuario"
                  value={username}
                  onChange={(e) => { setUsername(e.target.value); setErrors(p => ({ ...p, username: undefined })); }}
                  autoComplete="username"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  className="pl-10 h-11 text-base sm:text-sm sm:h-9"
                />
              </div>
              {errors.username && (
                <p className="text-xs text-red-500">{errors.username}</p>
              )}
            </div>

            {/* Password */}
            <div className="space-y-1">
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="Contraseña"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setErrors(p => ({ ...p, password: undefined })); }}
                  autoComplete="current-password"
                  className="pl-10 pr-10 h-11 text-base sm:text-sm sm:h-9"
                />
                <button
                  type="button"
                  tabIndex={-1}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  onClick={() => setShowPassword(v => !v)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors.password && (
                <p className="text-xs text-red-500">{errors.password}</p>
              )}
            </div>

            {/* Recuérdame */}
            <div className="flex items-center gap-2">
              <Checkbox
                id="remember"
                checked={remember}
                onCheckedChange={setRemember}
              />
              <label htmlFor="remember" className="text-sm text-gray-600 cursor-pointer select-none">
                Recuérdame
              </label>
            </div>

            {/* Submit */}
            <Button
              type="submit"
              loading={loading}
              className="w-full h-11 text-base sm:text-sm sm:h-9 font-semibold"
            >
              Iniciar Sesión
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
